import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogTrigger, DialogClose } from '@/components/ui/dialog'
import { toast } from 'sonner'
import {
  getCustomerByFinancialAccountId,
  createGoodwillAdjustment,
  createLoanLeaseAdjustment,
} from '@/api/rewards-grants'
import { Icons } from '@/theme/Images'
import { Divider } from '@/components/divider'
import RulesDialogHeader from '../dialog-header/rules-dialog-header'
import InputBlock from '../input-block'
import SelectBlock from '../select-block'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { ETDate } from '@/utils/et-date'
import { useQueryClient } from '@tanstack/react-query'

const CreateGrants = () => {
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirmClose, setShowConfirmClose] = useState(false)
  const [showConfirmTransfer, setShowConfirmTransfer] = useState(false)

  // Generate selectable months for dropdown
  const getLastSixMonths = () => {
    const months = []
    const today = ETDate.today()
    const currentDay = today.day

    // Get current hour in Eastern Time using the same method as ETDate
    const now = new Date()
    const timeZone = ETDate.TIME_ZONE
    const hourParts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      hour12: false,
    }).formatToParts(now)
    const currentHour = Number(hourParts.find(p => p.type === 'hour')?.value)

    // Determine starting point:
    // - Never include current month (i >= 1)
    // - If after the 4th at 4AM ET, start from 1 month ago (allow previous month)
    // - Otherwise, start from 2 months ago (skip current and previous month)
    const isAfter4AMOn4th = currentDay === 4 && currentHour >= 4
    const startOffset = currentDay > 4 || isAfter4AMOn4th ? 1 : 2

    for (let i = startOffset; i < startOffset + 6; i++) {
      const date = today.addMonths(-i)
      const year = date.year
      const month = String(date.month).padStart(2, '0')

      // Create a JS Date for formatting the month name
      const jsDate = new Date(date.year, date.month - 1, 1)
      const monthName = jsDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

      months.push({
        label: monthName,
        value: `${year}${month}`,
      })
    }

    return months.reverse()
  }

  const validationSchema = Yup.object({
    rewardReason: Yup.string()
      .required('Reward Reason is required')
      .oneOf(['GOODWILL', 'MANUAL_LOAN_LEASE'], 'Invalid reward reason'),
    correctedMonth: Yup.string().when('rewardReason', {
      is: 'MANUAL_LOAN_LEASE',
      then: schema => schema.required('Corrected Month is required for Manual loan lease'),
      otherwise: schema => schema.notRequired(),
    }),
    financialAccountId: Yup.string().required('Financial Account ID is required'),
    points: Yup.number()
      .required('Points is required')
      .test('not-zero', 'Points must be non-zero', value => {
        return value !== 0
      })
      .test('max-decimals', 'Points can have a maximum of 2 decimal places', value => {
        if (!value) return true // Let required validation handle empty values
        const decimalPlaces = value.toString().split('.')[1]?.length || 0
        return decimalPlaces <= 2
      }),
    comments: Yup.string().required('Comment is required'),
  })

  const formik = useFormik({
    initialValues: {
      rewardReason: '',
      correctedMonth: '',
      financialAccountId: '',
      points: '',
      comments: '',
    },
    validationSchema,
    onSubmit: async () => {
      // Show confirmation modal instead of submitting directly
      setShowConfirmTransfer(true)
    },
  })

  // Actually submit the transfer
  const handleConfirmTransfer = async () => {
    if (isLoading) return
    setIsLoading(true)

    try {
      const customerData = await getCustomerByFinancialAccountId(formik.values.financialAccountId)
      if (!customerData?.id) throw new Error('Customer ID missing from response')

      // Use comments as provided
      const comments = formik.values.comments || ''

      // Call appropriate API based on reward reason
      if (formik.values.rewardReason === 'GOODWILL') {
        await createGoodwillAdjustment({
          customerId: customerData.id,
          points: formik.values.points,
          comments: comments,
        })
      } else if (formik.values.rewardReason === 'MANUAL_LOAN_LEASE') {
        await createLoanLeaseAdjustment({
          customerId: customerData.id,
          points: formik.values.points,
          comments: comments,
          correctedMonth: formik.values.correctedMonth,
        })
      }

      setShowConfirmTransfer(false)
      setIsOpen(false)

      // Invalidate manual adjustments query to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['manualAdjustments'] })

      // Show success toast notification
      toast.success('Transfer successful', {
        description: `Points transferred successfully from Fasten to ${formik.values.financialAccountId}`,
      })

      // Reset form
      formik.resetForm()
    } catch (error) {
      console.error(error)
      toast.error(error.message || 'Failed to transfer points')
    } finally {
      setIsLoading(false)
    }
  }

  // Helper to check if any field has a value
  const hasAnyInput = () => {
    return (
      formik.values.rewardReason.trim() !== '' ||
      formik.values.correctedMonth.trim() !== '' ||
      formik.values.financialAccountId.trim() !== '' ||
      formik.values.points !== '' ||
      formik.values.comments.trim() !== ''
    )
  }

  // Auto-focus Reward Reason dropdown when modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the DOM is ready
      const timer = setTimeout(() => {
        // Find the SelectTrigger button for rewardReason
        const trigger = document.querySelector('[aria-label="Reward Reason*"]')
        if (trigger) {
          trigger.focus()
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Reset state when modal closes
  const handleOpenChange = open => {
    if (!open && hasAnyInput()) {
      // If trying to close and there's input, show confirmation instead
      setShowConfirmClose(true)
      return
    }

    setIsOpen(open)
    if (open) {
      // Reset form when opening the modal
      formik.resetForm()
    } else {
      // Reset form when closing
      formik.resetForm()
    }
  }

  // Intercept cancel: show confirm modal if any input, else close
  const handleCancel = e => {
    e?.preventDefault?.()
    if (hasAnyInput()) {
      setShowConfirmClose(true)
    } else {
      setIsOpen(false)
    }
  }

  // Actually close everything
  const handleConfirmClose = () => {
    setShowConfirmClose(false)
    setIsOpen(false)
    formik.resetForm()
  }

  const renderForm = () => (
    <div>
      <div className="flex flex-row gap-8 mb-6">
        <div className="w-[384px]">
          <SelectBlock
            label="Reward Reason*"
            id="rewardReason"
            placeholder="Select a reward reason"
            formik={formik}
            showValidation={true}
            description="Select the reason for this points grant."
            options={[
              { label: 'Goodwill', value: 'GOODWILL' },
              { label: 'Manual loan lease', value: 'MANUAL_LOAN_LEASE' },
            ]}
          />
        </div>
        {formik.values.rewardReason === 'MANUAL_LOAN_LEASE' && (
          <div className="w-[384px]">
            <SelectBlock
              label="Corrected Month*"
              id="correctedMonth"
              placeholder="Select the month this grant is correcting"
              formik={formik}
              showValidation={true}
              description="Select the month that this loan/lease grant is correcting."
              options={getLastSixMonths()}
            />
          </div>
        )}
      </div>
      <div className="flex flex-row gap-8">
        <div className="w-[384px]">
          <InputBlock
            label="Financial Account ID*"
            className="mt-1"
            id="financialAccountId"
            placeholder="ac_12345000000000000000000000000000"
            autoComplete="off"
            formik={formik}
            showValidation={false}
            icon={
              formik.errors.financialAccountId && formik.touched.financialAccountId ? (
                <Icons.FailureAlertIconSimple />
              ) : null
            }
            iconPosition="right"
          />
          <p className="text-sm text-[--gray-600] font-normal mt-1 mb-4">
            Enter the financial account ID from Highnote.
          </p>
          {formik.errors.financialAccountId && formik.touched.financialAccountId && (
            <p className="text-sm font-normal text-[--error-500] mb-4 -mt-2">{formik.errors.financialAccountId}</p>
          )}
        </div>
        <div className="w-[384px]">
          <InputBlock
            label="Points*"
            className="mt-1"
            id="points"
            placeholder="1000"
            type="number"
            step="0.01"
            min="0.01"
            autoComplete="off"
            formik={formik}
            showValidation={false}
            icon={formik.errors.points && formik.touched.points ? <Icons.FailureAlertIconSimple /> : null}
            iconPosition="right"
          />
          <p className="text-sm text-[--gray-600] font-normal mt-1 mb-4">Enter the number of points to grant.</p>
          {formik.errors.points && formik.touched.points && (
            <p className="text-sm font-normal text-[--error-500] mb-4 -mt-2">{formik.errors.points}</p>
          )}
        </div>
      </div>
      <div>
        <InputBlock
          label="Comment*"
          className="mt-1"
          id="comments"
          placeholder="Error on statement"
          autoComplete="off"
          formik={formik}
          showValidation={false}
          icon={formik.errors.comments && formik.touched.comments ? <Icons.FailureAlertIconSimple /> : null}
          iconPosition="right"
        />
        <p className="text-sm text-[--gray-600] font-normal mt-1">Describe why this points grant is being made.</p>
        {formik.errors.comments && formik.touched.comments && (
          <p className="text-sm font-normal text-[--error-500] mt-2">{formik.errors.comments}</p>
        )}
      </div>
    </div>
  )

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            className="bg-brand-600 hover:bg-brand-700 md:w-auto h-[36px] text-sm text-white"
            onClick={() => setIsOpen(true)}
          >
            <img src={Icons.plusIcon} alt="icon" height={11.67} width={11.67} />
            <span className="font-semibold text-sm ml-2">Grant Points</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-[880px] h-[90vh] flex flex-col">
          <RulesDialogHeader
            title="Grant Points"
            description="Perform a grant of points to the given user that may be redeemed immediately."
            icon={Icons.arrowCircleBrokenRight}
          />
          <div className="ml-[-26px] w-[106%]">
            <Divider />
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
            <form onSubmit={formik.handleSubmit} className="min-h-[300px]">
              {renderForm()}
            </form>
          </div>
          <div className="ml-[-26px] w-[106%]">
            <Divider />
          </div>
          <DialogFooter className="pt-2">
            <DialogClose asChild>
              <Button
                variant="outline"
                className="w-full md:w-[410px] h-[44px] text-[--gray-700]"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="text-white w-full md:w-[410px] h-[44px]"
              onClick={formik.handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Transferring...' : 'Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirm Close Modal */}
      <Dialog open={showConfirmClose} onOpenChange={setShowConfirmClose}>
        <DialogContent className="max-w-[480px]">
          <RulesDialogHeader
            title="Close without saving"
            description="Are you sure you close without saving? Any changes will be lost."
            icon={Icons.annotationAlertIcon}
          />
          <div className="ml-[-24px] w-[111%]">
            <Divider />
          </div>
          <DialogFooter className="w-full">
            <Button
              variant="outline"
              className="w-full md:w-full h-[44px] text-[--gray-700] mt-1"
              onClick={() => setShowConfirmClose(false)}
            >
              Back
            </Button>
            <Button className="text-white w-full h-[44px] mt-1" onClick={handleConfirmClose}>
              Close without saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirm Transfer Modal */}
      <Dialog open={showConfirmTransfer} onOpenChange={setShowConfirmTransfer}>
        <DialogContent className="max-w-[480px]">
          <RulesDialogHeader
            title="Confirm Transfer"
            description="Review the details below before submitting this point transfer:"
            icon={Icons.clipboardCheckIcon}
          />
          <div className="ml-[-24px] w-[111%]">
            <Divider />
          </div>
          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <span className="text-gray-900 text-sm">•</span>
              <span className="text-gray-900 text-sm">
                {formik.values.points} {parseFloat(formik.values.points) === 1 ? 'point' : 'points'}
              </span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-gray-900 text-sm">•</span>
              <span className="text-gray-900 text-sm">to {formik.values.financialAccountId}</span>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              This action will update the ledgers and be recorded in the audit log.
            </p>
          </div>
          <div className="ml-[-24px] w-[111%]">
            <Divider />
          </div>
          <DialogFooter className="w-full">
            <Button
              variant="outline"
              className="w-full md:w-full h-[44px] text-[--gray-700] mt-1"
              onClick={() => setShowConfirmTransfer(false)}
            >
              Cancel
            </Button>
            <Button className="text-white w-full h-[44px] mt-1" onClick={handleConfirmTransfer} disabled={isLoading}>
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CreateGrants
